import React from "react";
import {Box} from "@mui/material";
import {Typography} from "@mui/joy";
import {DraftOutlined} from "@mui-symbols-material/w300";
import {FileListItem} from "../FileList";
import {isImageFile} from "../../utils/fileType";

interface Props {
    file: FileListItem;
    /** Width of the thumbnail frame (default 48). Accepts e.g. "100%" for tiles. */
    width?: number | string;
    /** Height of the thumbnail frame (default 48). Accepts e.g. "100%". */
    height?: number | string;
    /** How the preview image fits its frame (default "cover"). */
    objectFit?: "cover" | "contain";
    /** Size of the fallback file icon for non-previewable files (default 48). */
    iconSize?: number;
    /** Corner radius of the preview image (default 4). Use 0 when an outer box clips the corners. */
    borderRadius?: number | string;
}

/**
 * Thumbnail for a file entry.
 * - Images with a thumbnail render the preview image.
 * - Everything else renders an outlined file icon with the extension label.
 */
const FileThumbnail = ({file, width = 48, height = 48, objectFit = "cover", iconSize = 48, borderRadius = 4}: Props) => {
    const isImage = isImageFile(file.contentType);

    if (isImage && file.thumbnailLink) {
        return (
            <img
                src={file.thumbnailLink}
                alt={file.filename}
                style={{
                    width,
                    height,
                    objectFit,
                    borderRadius,
                    flexShrink: 0,
                    backgroundColor: objectFit === "contain" ? "#f7f7f7" : undefined,
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                position: "relative",
                width,
                height,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "#666",
            }}
        >
            <DraftOutlined sx={{fontSize: iconSize, color: "#999"}} />
            <Typography
                level="captionBold"
                sx={{
                    position: "absolute",
                    top: "58%",
                    left: 0,
                    right: 0,
                    transform: "translateY(-50%)",
                    textAlign: "center",
                    fontSize: iconSize <= 48 ? "9px" : "13px",
                    lineHeight: 1,
                    color: "#666",
                    pointerEvents: "none",
                }}
            >
                {file.extension ? file.extension.toUpperCase().slice(0, 4) : "FILE"}
            </Typography>
        </Box>
    );
};

export default FileThumbnail;
